(ns common.form.core
  (:require [clojure.spec.alpha :as spec]
            [common.util :as util]
            [phrase.alpha :as p]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defn build-path
  [& path]
  (vec (flatten path)))

(defn visited-field-in-error?
  [{:keys [errors meta]}]
  (->> errors
       (util/path-vals)
       (map first)
       (map (partial get-in meta))
       (some :visited)
       (boolean)))

(defn get-validate-form-fn
  "Get function that updates the db with the errors in the form

   `namespace` Unique keyword for qualifying form events and subscriptions
   `spec`      Spec that is used to define data structure of the form value"
  [namespace spec]
  (fn [db]
    (let [problems (keep #(p/phrase {} %)
                         (::spec/problems
                          (spec/explain-data spec
                                             (get-in db [namespace :value]))))
          grouped (dissoc (group-by :in problems) nil)
          grouped-by-key (dissoc (group-by :key problems) nil)
          multi-error-group (apply merge
                                   (flatten (for [[k v] (group-by :ins problems)]
                                              (when k
                                                (mapv (fn [x] {x v}) k)))))]
      (->> (merge multi-error-group grouped grouped-by-key)
           (reduce-kv (fn [acc key val]
                        (assoc-in acc key val))
                      {})
           (assoc-in db [namespace :errors])))))

(defhook use-error-message
  "Returns error message under `path`

   `namespace`       Unique keyword for qualifying form events and subscriptions
   `path`            Path in form value that maps to the items"
  [namespace path]
  (use-subscribe [(keyword namespace :error-message) path]))

(defhook use-bind-control
  "Returns props for the input field,
   to control it through the re-frame state

   `namespace`       Unique keyword for qualifying form events and subscriptions
   `path`            Path in form value that maps to the items
   `on-change-event` Form's on-change event
   `f`               Format function to format the value before putting it in db"
  [namespace path {:keys [on-change-event value-sub f]
                   :or {on-change-event :on-change
                        value-sub :value
                        f identity}}]
  (let [extract-value (fn [^js event]
                        (if (and event (instance? js/Event (.-nativeEvent event)))
                          (-> event .-target .-value f)
                          (f event)))
        on-change (uix/use-callback (fn [event] (rf/dispatch [(keyword namespace on-change-event) path
                                                              (extract-value event)]))
                                    [extract-value on-change-event namespace path])
        on-blur (uix/use-callback (fn [] (rf/dispatch [(keyword namespace :on-blur) path]))
                                  [namespace path])
        on-focus (uix/use-callback (fn [] (rf/dispatch [(keyword namespace :on-focus) path]))
                                   [namespace path])
        value (use-subscribe [(keyword namespace value-sub) path])
        error (boolean (use-error-message namespace path))]
    (uix/use-memo (fn []
                    {:on-change on-change
                     :on-blur on-blur
                     :on-focus on-focus
                     :value value
                     :error error
                     :path path})
                  [on-change on-blur on-focus value error path])))

(defn register-events
  "Register events to manipulate form state."
  [qualify namespace validate-form initial-value forward-events submit-event cancel-event on-change on-change-event]
  ;; :namespace/init
  ;; Initialize the form state.
  (rf/reg-event-fx (qualify :init)
    [validate-form]
    (fn [{:keys [db]} [_ value]]
      {:db (update-in db [namespace :value] #(or % value initial-value))
       :forward-events forward-events}))

  ;; :namespace/destroy
  ;; Removes the state and forward-events of the form.
  (rf/reg-event-fx (qualify :destroy)
    (fn [{:keys [db]} _]
      {:db (dissoc db namespace)
       :forward-events (sequence (comp (map :register)
                                       (map (partial hash-map :unregister)))
                                 forward-events)}))

  ;; :namespace/submit
  ;; When there is no errors in the form call the `submit-event`
  (rf/reg-event-fx (qualify :submit)
    [(rf/inject-cofx :sub (qualify :errors?))]
    (fn [{errors? (qualify :errors?) db :db} [_ & args]]
      {:db db
       :dispatch (if-not errors?
                   (vec (concat [submit-event (get-in db [namespace :value])] args))
                   [(qualify :show-all-errors)])}))

  ;; :namespace/cancel
  ;; Cancel editing
  (rf/reg-event-fx (qualify :cancel)
    (fn [{:keys [db]}]
      {:db (assoc-in db [namespace :meta] nil)
       :dispatch [cancel-event]}))

  ;; :namespace/on-change
  ;; Updates the value of the form on change
  (rf/reg-event-fx (qualify :on-change)
    [validate-form]
    (fn [{:keys [db]} [_ path value]]
      {:db (cond-> db
             true (assoc-in (build-path namespace :value path) value)
             on-change (on-change path value))
       :fx (cond-> []
             on-change-event (conj [:dispatch [on-change-event value]]))}))

  ;; :namespace/show-all-errors
  ;; Sets the show-all-errors flags to true
  (rf/reg-event-db (qualify :show-all-errors)
    (fn [db]
      (assoc-in db (build-path namespace :meta :show-all-errors?) true)))

  ;; :namespace/on-blur
  ;; Sets the input field to dirty
  (rf/reg-event-db (qualify :on-blur)
    (fn [db [_ path]]
      (assoc-in db (build-path namespace :meta path) {:focused false :visited true})))

  ;; :namespace/on-focus
  ;; Sets the input field to focused
  (rf/reg-event-db (qualify :on-focus)
    (fn [db [_ path]]
      (assoc-in db (build-path namespace :meta path :focused) true))))

(defn register-subs
  "Register subscriptions to retrieve form state."
  [qualify namespace]
  ;; :namespace/form
  ;; Get value in the form state
  (rf/reg-sub (qualify :form)
    (fn [db _]
      (get db namespace)))

  ;; :namespace/value
  ;; Get value in the form state under `path`
  (rf/reg-sub (qualify :value)
    :<- [(qualify :form)]
    (fn [form [_ path]]
      (get-in form (build-path :value path))))

  ;; :namespace/value-count
  ;; Get the amount of items in the form state under `path`
  (rf/reg-sub (qualify :value-count)
    (fn [[_ path]]
      (rf/subscribe [(qualify :value) path]))
    (fn [value]
      (count value)))

  ;; :namespace/value-ids
  ;; Get all the ids in the form state under `path`
  (rf/reg-sub (qualify :value-ids)
    (fn [[_ path]]
      (rf/subscribe [(qualify :value) path]))
    (fn [value]
      (mapv :id value)))

  ;; :namespace/meta
  ;; Get the value of the `meta` in form state
  (rf/reg-sub (qualify :meta)
    :<- [(qualify :form)]
    (fn [form [_ & path]]
      (get-in form (build-path :meta path))))

  ;; :namespace/show-all-errors
  ;; Show-all-errors flag
  (rf/reg-sub (qualify :show-all-errors?)
    (fn [db]
      (get-in db (build-path namespace :meta :show-all-errors?))))

  ;; :namespace/errors
  ;; Get raw errors in form state
  (rf/reg-sub (qualify :errors)
    (fn [db _]
      (get-in db [namespace :errors])))

  ;; :namespace/error
  ;; Get errors in form state under `path`
  (rf/reg-sub (qualify :error)
    :<- [(qualify :errors)]
    (fn [errors [_ path]]
      (get-in errors path)))

  ;; :namespace/error-message
  ;; Get single error-message in form state under `path`
  ;; This error-message is only returned if `show-all-errors?` or the path has been visited.
  (rf/reg-sub (qualify :error-message)
    (fn [[_ path]]
      [(rf/subscribe [(qualify :show-all-errors?)])
       (rf/subscribe (build-path (qualify :meta) path :visited))
       (rf/subscribe [(qualify :error) path])])
    (fn [[show-all-errors? visited? error] _]
      (when (or show-all-errors? visited?)
        (first (map :message error)))))

  ;; :namespace/errors?
  ;; Returns true if there are errors in form state - all or visited fields.
  (rf/reg-sub (qualify :errors?)
    :<- [(qualify :form)]
    (fn [form [_ visited?]]
      (if visited?
        (visited-field-in-error? form)
        (-> form :errors seq boolean)))))

(defn register-form
  "Register event handlers and subscriptions to manage the state for a form.
   See `register-events` and `register-subs` for documentation on which events
   and subscriptions are created. Accepts a configuration map with keys:

  `namespace`             Unique keyword for qualifying form events and subscriptions.
  `initial-value`         Initial form state.
  `validate-form`         Effect to validate the form value
  `forward-events`        List of events to listen to and to post to
  `submit-event`          Event to call on form submit
  `on-change`             Optional function to modify app db on-change
  `on-change-event`       Optional event to be dispatched on-change

  Usage:
    (register-form
      {:namespace      :form.product-foo
       :initial-value  {}
       :validate-form  fn-validate-form-foo
       :forward-events [{:events #{:resource.foo/get-foo-success}
                         :dispatch-to [:form.product-foo/set-foo-value]}]
       :submit-event   :form.product-foo/confirm-edit"
  [{:keys [namespace initial-value validate-form forward-events submit-event cancel-event on-change on-change-event]}]
  (let [qualify (fn [k] (keyword namespace k))
        forward-events* (mapv #(assoc % :register (str (random-uuid))) forward-events)]
    (register-events qualify namespace validate-form initial-value forward-events* submit-event cancel-event on-change on-change-event)
    (register-subs qualify namespace)))
